import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationSidebar from '../../components/ui/NavigationSidebar';
import CategoryFilters from './components/CategoryFilters';
import CategoryTable from './components/CategoryTable';
import CategoryModal from './components/CategoryModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import BulkActions from './components/BulkActions';
import Icon from '../../components/AppIcon';
import { logout } from '../../utils/auth';

const ProductCategoriesManagement = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole] = useState('admin');
  const [isAuthenticated] = useState(true);
  

  // Categories data state
 const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');


  const flattenCategories = (tree) => {
  const result = [];
  const traverse = (node, parentCategory = '') => {
    result.push({
      ...node,
      parentCategory,
      children: undefined // Remove children for table
    });
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child, node.name));
    }
  };
  tree.forEach(node => traverse(node));
  return result;
};



const fetchActiveCategories = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('_id');

    // Pick base URL depending on userId
    const baseUrl =
      userId === "56"
        ? import.meta.env.VITE_CATDEMO_BASE_URL
        : import.meta.env.VITE_CATPROD_BASE_URL;

    const response = await fetch(`${baseUrl}/category/active`, {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      const mapped = data.map((cat) => ({
        id: String(cat.categoryId),
        name: cat.name,
        parentId: String(cat.parentInt),
        level: cat.level,
        description: cat.description || ''
      }));

        
      const idToNode = {};
      mapped.forEach(cat => {
        idToNode[cat.id] = { ...cat, children: [] };
      });

      const tree = [];
      mapped.forEach(cat => {
        if (cat.parentId !== "0" && idToNode[cat.parentId]) {
          idToNode[cat.parentId].children.push(idToNode[cat.id]);
        } else {
          tree.push(idToNode[cat.id]);
        }
      });

      const flat = flattenCategories(tree);
      const flatWithSerialNo = flat.map((cat, idx) => ({
        ...cat,
        serialNo: idx + 1
      }));

      setCategories(flatWithSerialNo);
      setFilteredCategories(flatWithSerialNo);
    } else {
      setCategories([]);
      setFilteredCategories([]);
    }
  } catch (error) {
    setCategories([]);
    setFilteredCategories([]);
  }
};


useEffect(() => {
  fetchActiveCategories();
}, []);



  // Handle search and filtering
  useEffect(() => {
    let filtered = categories?.filter(category => {
      const matchesSearch = category?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           category?.parentCategory?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesLevel = levelFilter === 'all' || category?.level?.toString() === levelFilter;
      return matchesSearch && matchesLevel;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        
        if (sortConfig?.key === 'level' || sortConfig?.key === 'serialNo') {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCategories(filtered);
  }, [categories, searchTerm, levelFilter, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle category selection
  const handleSelectCategory = (categoryId, isSelected) => {
    setSelectedCategories(prev => 
      isSelected 
        ? [...prev, categoryId]
        : prev?.filter(id => id !== categoryId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedCategories(isSelected ? filteredCategories?.map(cat => cat?.id) : []);
  };

  // Handle category creation/editing
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

const handleEditCategory = async (category) => {
  try {
    setIsSubmitting(true);
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('_id');

    // Pick base URL depending on userId
    const baseUrl =
      userId === "56"
        ? import.meta.env.VITE_CATDEMO_BASE_URL
        : import.meta.env.VITE_CATPROD_BASE_URL;

    const response = await fetch(`${baseUrl}/category/${category.id}`, {
      method: 'GET',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      if (result?.status === "success" && result?.data) {
        // Map API response to your category structure
        setEditingCategory({
          id: String(result.data.categoryId),
          name: result.data.name,
          parentId: String(result.data.parentInt),
          description: result.data.description || '',
          level: result.data.level
        });
        setShowCategoryModal(true);
      } else {
        alert(result?.message || "Failed to fetch category details");
      }
    } else {
      alert("Failed to fetch category details");
    }
  } catch (error) {
    alert("Error fetching category details");
  } finally {
    setIsSubmitting(false);
  }
};

const handleCategorySubmit = async (formData) => {
  try {
    setIsSubmitting(true);
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('_id');

    // Pick base URL depending on userId
    const baseUrl =
      userId === "56"
        ? import.meta.env.VITE_CATDEMO_BASE_URL
        : import.meta.env.VITE_CATPROD_BASE_URL;

    if (editingCategory) {
      // EDIT: Use PUT API
      const parentCat = categories?.find(c => c?.id === formData?.parentCategory);
      const payload = {
        client_category_id: editingCategory.id,
        category_id: 1,
        category: formData?.name,
        description: formData?.description,
        parent_category_id: parentCat ? parseInt(parentCat.id) : 0,
        level: parentCat ? parentCat.level + 1 : 1,
        levels: 2
      };

      const response = await fetch(`${baseUrl}/category/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok && result?.status === "success") {
        await fetchActiveCategories();
        setSuccessMessage('Category updated successfully!');
      } else {
        alert(result?.message || 'Failed to update category');
      }
    } else {
      // CREATE: Use POST API
      const parentCat = categories?.find(c => c?.id === formData?.parentCategory);
      const payload = {
        category_id: 1,
        category: formData?.name,
        description: formData?.description,
        parent_category_id: parentCat ? parseInt(parentCat.id) : 0,
        level: parentCat ? parentCat.level + 1 : 1,
        levels: 2
      };

      const response = await fetch(`${baseUrl}/category`, {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok && result?.status === "success") {
        await fetchActiveCategories();
        setSuccessMessage('Category created successfully!');
      } else {
        alert(result?.message || 'Failed to create category');
      }
    }

    setShowCategoryModal(false);
  } catch (error) {
    console.error('Error saving category:', error);
    throw error;
  } finally {
    setIsSubmitting(false);
  }
};


  // Handle category deletion
  const handleDeleteCategory = (category) => {
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (category, deleteAction) => {
    try {
      if (deleteAction === 'delete-with-children') {
        // Delete category and all its descendants
        const getDescendants = (parentId) => {
          const children = categories?.filter(cat => cat?.parentId === parentId);
          let descendants = [...children];
          children?.forEach(child => {
            descendants = [...descendants, ...getDescendants(child?.id)];
          });
          return descendants;
        };

        const toDelete = [category, ...getDescendants(category?.id)];
        const idsToDelete = toDelete?.map(cat => cat?.id);
        setCategories(prev => prev?.filter(cat => !idsToDelete?.includes(cat?.id)));
      } else if (deleteAction === 'move-children') {
        // Delete category but move direct children to its parent
        const directChildren = categories?.filter(cat => cat?.parentId === category?.id);
        const updatedCategories = categories?.filter(cat => cat?.id !== category?.id)?.map(cat => {
            if (directChildren?.some(child => child?.id === cat?.id)) {
              return {
                ...cat,
                parentId: category?.parentId,
                parentCategory: category?.parentCategory,
                level: category?.level
              };
            }
            return cat;
          });
        setCategories(updatedCategories);
      } else {
        // Simple delete (only if no children)
        setCategories(prev => prev?.filter(cat => cat?.id !== category?.id));
      }
      
      setSelectedCategories(prev => prev?.filter(id => id !== category?.id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  // Handle bulk operations
  const handleBulkDelete = () => {
    setCategories(prev => prev?.filter(cat => !selectedCategories?.includes(cat?.id)));
    setSelectedCategories([]);
  };

  const handleBulkExport = () => {
    const selectedCats = categories?.filter(cat => selectedCategories?.includes(cat?.id));
    const csvContent = [
      ['S.No', 'Category', 'Parent Category', 'Level', 'Description'],
      ...selectedCats?.map(cat => [
        cat?.serialNo,
        cat?.name,
        cat?.parentCategory || 'Root',
        cat?.level,
        cat?.description || ''
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `categories-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleBulkMove = (targetParentId) => {
    const targetParent = categories?.find(cat => cat?.id === targetParentId);
    const newLevel = targetParent ? targetParent?.level + 1 : 1;
    
    setCategories(prev => prev?.map(cat => 
      selectedCategories?.includes(cat?.id)
        ? {
            ...cat,
            parentId: targetParentId,
            parentCategory: targetParent?.name || '',
            level: newLevel
          }
        : cat
    ));
    setSelectedCategories([]);
  };

  // Handle Excel export
  const handleExportExcel = () => {
    const csvContent = [
      ['S.No', 'Category', 'Parent Category', 'Level', 'Description'],
      ...filteredCategories?.map(cat => [
        cat?.serialNo,
        cat?.name,
        cat?.parentCategory || 'Root',
        cat?.level,
        cat?.description || ''
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-categories-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  // const handleLogout = () => {
  //   navigate('/admin-login');
  // };

  // Logout handler
        const handleLogout = (e) => {
          e.preventDefault();
          logout(navigate);
        };


  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Sidebar */}
      <NavigationSidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        userRole={userRole}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
      />
      {/* Main Content */}
      <div className={`
        transition-all duration-300 ease-in-out
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-80'}
      `}>
        {/* Header */}
        {/* <header className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Menu" size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Product Categories</h1>
                <p className="text-sm text-muted-foreground">
                  Manage your product category hierarchy and organization
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">Administrator</p>
                <p className="text-xs text-muted-foreground">Full Access</p>
              </div>
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
            </div>
          </div>
        </header> */}

        {/* Content */}
        <main className="p-6 space-y-6">
          {/* Filters */}
          <CategoryFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            levelFilter={levelFilter}
            onLevelFilterChange={setLevelFilter}
            onExportExcel={handleExportExcel}
            onCreateCategory={handleCreateCategory}
            totalCategories={categories?.length}
            filteredCount={filteredCategories?.length}
          />

          {/* Categories Table */}
          <CategoryTable
            categories={filteredCategories}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
            searchTerm={searchTerm}
            levelFilter={levelFilter}
            sortConfig={sortConfig}
            onSort={handleSort}
            selectedCategories={selectedCategories}
            onSelectCategory={handleSelectCategory}
            onSelectAll={handleSelectAll}
          />

          {/* Bulk Actions */}
          <BulkActions
            selectedCount={selectedCategories?.length}
            onBulkDelete={handleBulkDelete}
            onBulkExport={handleBulkExport}
            onBulkMove={handleBulkMove}
            onClearSelection={() => setSelectedCategories([])}
            categories={categories}
          />
        </main>
      </div>
      {/* Modals */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSubmit={handleCategorySubmit}
        category={editingCategory}
        categories={categories}
        mode={editingCategory ? 'edit' : 'create'}
      />
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        category={deletingCategory}
        categories={categories}
      />
      {successMessage && (
  <div className="fixed top-6 right-6 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in">
    <Icon name="CheckCircle" size={24} color="white" />
    <span>{successMessage}</span>
    <button
      className="ml-4 text-white hover:text-gray-200"
      onClick={() => setSuccessMessage('')}
    >
      <Icon name="X" size={18} />
    </button>
  </div>
)}
      

    </div>
  );
};

export default ProductCategoriesManagement;